import { type FC, useState } from "react";
import { Icon } from "~/common/components/icon/Icon";
import { IconType } from "~/common/components/icon/IconType";
import { Link, LinkButton } from "~/common/components/Link";
import { Trans, useTranslation } from "react-i18next";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import LoginPopup from "~/features/auth/login/components/LoginPopup";
import { userIsRegistered } from "~/domains/user/utils/roles";
import {
  isDatasheetPage,
  redirectToUrl,
  userOnMyAccountPage,
  userOnRegistrationConfirmatioAccountPage,
  userOnRegistrationConfirmationEmailPage,
} from "~/utils/url/urlUtils";
import { CART_PAGE, REGISTER_PAGE } from "~/utils/url/staticUrls";
import { useUndoPartnerPopupConfirmation } from "~/features/affiliate/components/AffiliateSalesPartnerBanner/components/UndoPartnerPopupConfirmation";
import {
  displayAffiliatePartnerPopupBanner,
  saveAffiliatePartnerBannerClosedCookie,
  showAffiliatePartnerSaveBtn,
  transformSalesPartnerDTO,
  useShowAffiliateMainBanner,
} from "~/domains/affiliate/utils/utils";
import { useOnMount } from "~/common/hooks/useOnMount";
import {
  trackBannerClose,
  trackBannerOnLoad,
  trackBannerRequestOfferClick,
  trackBannerSave,
  trackBannerUndo,
} from "~/app/analytics/ga/affiliate";
import { AffiliateRequestOfferBanner } from "~/features/affiliate/components/AffiliateSalesPartnerBanner/components/AffiliateRequestOfferBanner";
import { AffiliateRegisterBanner } from "~/features/affiliate/components/AffiliateSalesPartnerBanner/components/AffiliateRegisterBanner";
import { AffiliateSavePartnerTile } from "~/features/affiliate/components/AffiliateSalesPartnerBanner/components/AffiliateSavePartnerTile";

import classes from "~/features/affiliate/components/AffiliateSalesPartnerBanner/AffiliateSalesPartnerBanner.module.less";
import classNames from "classnames";
import { useIsMinDesktopSize, useIsMinPhoneSize } from "~/common/hooks/useMediaQuery";
import { Button } from "~/common/mui";
import {
  selectPreviousSavedAffiliate,
  selectPreviousSavedSubsidiaryId,
  selectSavedAffiliate,
  selectVisitedAffiliate,
} from "~/domains/affiliate/redux/selectors";
import { useDispatch, useSelector } from "react-redux";
import { getBannerAffiliatePartner_toBeRemoved } from "~/domains/affiliate/utils/utilsToRefactorOut";
import { saveVisitedSalesPartnerThunk, undoSaveAffiliateThunk } from "~/domains/affiliate/redux/actions";
import { Alert } from "~/common/mui/Alert/Alert";

const AffiliateSalesPartnerBanner: FC = () => {
  const { t } = useTranslation();
  const [showBanner, setShowBanner] = useState<boolean>(useShowAffiliateMainBanner());

  if (!showBanner) {
    return null;
  }

  const isRegistered = userIsRegistered();

  const visitedAffiliate = useSelector(selectVisitedAffiliate);
  const previousSavedAffiliate = useSelector(selectPreviousSavedAffiliate);
  const previousSavedSubsidiaryId = useSelector(selectPreviousSavedSubsidiaryId);
  const savedAffiliate = useSelector(selectSavedAffiliate);

  const isSaved = !!visitedAffiliate && savedAffiliate?.affiliateId === visitedAffiliate?.affiliateId;

  if (!visitedAffiliate) {
    return null;
  }

  useOnMount(() => {
    if (!isSaved) {
      trackBannerOnLoad();
    }
  });

  const [showSaveButton, setShowSaveButton] = useState<boolean>(!!showAffiliatePartnerSaveBtn());
  const { val: loginPopupOpen, setTrue: openLogin, setFalse: closeLogin } = useBooleanState(false);

  const dispatch = useDispatch();

  const saveSalesPartner = async () => {
    const success = await dispatch(saveVisitedSalesPartnerThunk());
    if (success) {
      setShowSaveButton(false);
    }
    trackBannerSave();
  };

  const undoSalesPartner = async () => {
    const success = await dispatch(undoSaveAffiliateThunk());
    if (success) {
      setShowSaveButton(true);
      trackBannerUndo();
      close();
    }
  };

  const {
    open: openUndoModal,
    modal: undoModal,
    close,
  } = useUndoPartnerPopupConfirmation({
    onOk: undoSalesPartner,
    onCancel: () => close(),
  });

  const closeBanner = () => {
    setShowBanner(false);
    trackBannerClose();
    saveAffiliatePartnerBannerClosedCookie(visitedAffiliate.affiliateId);
  };

  const isDesktop = useIsMinDesktopSize();
  const isMobile = useIsMinPhoneSize();

  const isCheckout = window.location.pathname.includes(CART_PAGE);

  const miniBanner = (
    <div className={classNames(classes.miniBanner, classes.mobileContainer, isDesktop && "container")}>
      <div className={classes.imageContainer}>
        {isMobile && <img className={classes.logo} src={visitedAffiliate.logo}></img>}
        <h3 className={classNames(classes.title, !isSaved ? "mb-4" : "mb-0")}>
          {t("affiliateSalesPartner.banner.text.welcome")} <strong>{visitedAffiliate.name}</strong>
        </h3>
      </div>
      {!isCheckout && (
        <Button
          className={classNames(classes.button)}
          onClick={() => {
            trackBannerRequestOfferClick();
            redirectToUrl(CART_PAGE);
          }}
          data-test-id={"banner-partner-request-offer"}
        >
          {t("affiliateSalesPartner.banner.button.requestOffer")}
        </Button>
      )}
    </div>
  );

  if (isSaved && isRegistered) {
    return miniBanner;
  }

  return (
    <>
      <div className={classNames(!isDesktop ? classes.mobileContainer : "container mb-30")}>
        <div className={classes.banner}>
          <h3 className={classes.title}>
            {t("affiliateSalesPartner.banner.text.welcome")} <strong>{visitedAffiliate.name}</strong>
          </h3>

          <LinkButton className={classes.closeBtn} onClick={closeBanner}>
            <Icon type={IconType.Close} />
          </LinkButton>

          <AffiliateSavePartnerTile
            className={classes.affiliateTile}
            onSaveClick={saveSalesPartner}
            onUndoClick={openUndoModal}
            affiliate={transformSalesPartnerDTO(visitedAffiliate)}
            isSaved={!showSaveButton}
          />

          {isRegistered && !isSaved && !isCheckout && (
            <AffiliateRequestOfferBanner className={classes.salesOfferTile} />
          )}

          {isRegistered && !showSaveButton && (
            <p className={classes.saveResponse}>{t("affiliateSalesPartner.banner.message.changeAccount")}</p>
          )}

          {!isRegistered && <AffiliateRegisterBanner className={classes.salesOfferTile} />}

          {!isRegistered && !showSaveButton && (
            <Alert severity={"info"} className={classes.saveResponse}>
              <Trans i18nKey={"affiliateSalesPartner.banner.message.login"}>
                Please
                <Link href={REGISTER_PAGE} className={"link-sign-in"}>
                  register
                </Link>
                or
                <Link onClick={openLogin}>sign in</Link>
                to save permanently
              </Trans>
            </Alert>
          )}
        </div>
      </div>
      {loginPopupOpen && <LoginPopup onResetClick={() => {}} close={closeLogin} initialEmail={""} />}
      {undoModal}
    </>
  );
};

export function createAffiliateSalesPartnerBanner() {
  // guard
  if (!displayAffiliatePartnerPopupBanner()) {
    return null;
  }
  if (
    userOnRegistrationConfirmationEmailPage() ||
    userOnRegistrationConfirmatioAccountPage() ||
    userOnMyAccountPage() ||
    isDatasheetPage()
  ) {
    return null;
  }

  const partner = getBannerAffiliatePartner_toBeRemoved();
  if (!partner) {
    return null;
  }

  return <AffiliateSalesPartnerBanner />;
}
